import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Mail, Lock, User, Building2, Phone, MapPin, Eye, EyeOff, CheckCircle, AlertTriangle, Trophy, Swords } from 'lucide-react';
import { getErrorMessage } from '../utils/errorUtils';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email.toLowerCase(), formData.password);
      toast.success('Welcome back!');
      // Redirect superadmin to admin panel
      if (result.user.role === 'superadmin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
              alt="Black Arrow MMA"
              className="h-16 w-auto"
            />
          </Link>

          <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-2">
            Welcome Back
          </h2>
          <p className="text-zinc-400 mb-8">Sign in to your account</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="coach@gym.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-bold tracking-wide"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-zinc-500 hover:text-red-500 text-sm transition-colors">
              Forgot your password?
            </Link>
          </div>

          <p className="mt-6 text-center text-zinc-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-red-500 hover:text-red-400 font-medium">
              Register your gym
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] to-transparent z-10" />
        <img
          src="https://images.unsplash.com/photo-1737381508529-a110d717d5a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="MMA Training"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    gym_name: '',
    gym_address: '',
    gym_phone: '',
    sport_type: 'mma'
  });
  
  // Founders pricing state
  const [foundersData, setFoundersData] = useState({
    isFounders: true,
    spotsRemaining: 25
  });

  // Fetch founders status on mount
  React.useEffect(() => {
    const fetchFoundersStatus = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/founders/status`);
        const data = await response.json();
        setFoundersData({
          isFounders: data.is_active,
          spotsRemaining: data.spots_remaining
        });
      } catch (error) {
        console.error('Failed to fetch founders status:', error);
      }
    };
    fetchFoundersStatus();
  }, []);

  // Dynamic pricing based on founders status
  const sportTypes = foundersData.isFounders ? [
    { id: 'bjj', name: 'BJJ Only', price: '$129.99/mo', originalPrice: '$199.99', description: 'Brazilian Jiu-Jitsu focused', icon: '🥋', savings: '35%' },
    { id: 'mma', name: 'MMA', price: '$159.99/mo', originalPrice: '$299.99', description: 'Mixed Martial Arts', icon: '🥊', savings: '47%' },
    { id: 'hybrid', name: 'Hybrid', price: '$199.99/mo', originalPrice: '$399.99', description: 'BJJ + MMA Combined', icon: '👑', savings: '50%' },
  ] : [
    { id: 'bjj', name: 'BJJ Only', price: '$199.99/mo', description: 'Brazilian Jiu-Jitsu focused', icon: '🥋' },
    { id: 'mma', name: 'MMA', price: '$299.99/mo', description: 'Mixed Martial Arts', icon: '🥊' },
    { id: 'hybrid', name: 'Hybrid', price: '$399.99/mo', description: 'BJJ + MMA Combined', icon: '👑' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-l from-[#09090b] to-transparent z-10" />
        <img
          src="https://images.unsplash.com/photo-1561532325-7d5231a2dede?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Boxing Gloves"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md py-8"
        >
          <Link to="/" className="flex items-center justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
              alt="Black Arrow MMA"
              className="h-16 w-auto"
            />
          </Link>

          <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-2">
            Register Your Gym
          </h2>
          <p className="text-zinc-400 mb-8">Start managing your fighters today</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Your Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-name-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="coach@gym.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-email-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-5 mt-5">
              <p className="text-zinc-400 text-sm mb-4">Gym Information</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gym_name" className="text-zinc-300">Gym Name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="gym_name"
                  type="text"
                  placeholder="Elite MMA Academy"
                  value={formData.gym_name}
                  onChange={(e) => setFormData({ ...formData, gym_name: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-gym-name-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gym_address" className="text-zinc-300">Gym Address (Optional)</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="gym_address"
                  type="text"
                  placeholder="123 Fight Street"
                  value={formData.gym_address}
                  onChange={(e) => setFormData({ ...formData, gym_address: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-gym-address-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gym_phone" className="text-zinc-300">Gym Phone (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="gym_phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.gym_phone}
                  onChange={(e) => setFormData({ ...formData, gym_phone: e.target.value })}
                  className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 focus:ring-red-600"
                  data-testid="register-gym-phone-input"
                />
              </div>
            </div>

            {/* Sport Type Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Select Your Plan</Label>
                {foundersData.isFounders && foundersData.spotsRemaining > 0 && (
                  <span className="text-xs bg-yellow-600/20 text-yellow-500 px-2 py-1 rounded-sm border border-yellow-600/30">
                    🏆 {foundersData.spotsRemaining} Founding Spots Left
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                {sportTypes.map((sport) => (
                  <button
                    key={sport.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, sport_type: sport.id })}
                    className={`relative flex items-center gap-4 p-4 rounded-sm border transition-all ${
                      formData.sport_type === sport.id
                        ? 'border-red-600 bg-red-600/10'
                        : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                    }`}
                    data-testid={`sport-type-${sport.id}`}
                  >
                    <span className="text-2xl">{sport.icon}</span>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between">
                        <span className="font-heading font-bold text-white uppercase">{sport.name}</span>
                        <div className="text-right">
                          <span className="text-red-500 font-bold">{sport.price}</span>
                          {sport.originalPrice && (
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500 line-through text-xs">{sport.originalPrice}</span>
                              <span className="text-yellow-500 text-xs font-bold">Save {sport.savings}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-zinc-500">{sport.description}</span>
                    </div>
                    {formData.sport_type === sport.id && (
                      <CheckCircle className="w-5 h-5 text-red-500 absolute right-4 top-1/2 -translate-y-1/2" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-zinc-500">
                {foundersData.isFounders 
                  ? '🏆 Founding member pricing locked in for life! 30-day free trial included.'
                  : '30-day free trial included. Cancel anytime.'
                }
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-bold tracking-wide"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="mt-8 text-center text-zinc-400">
            Already have an account?{' '}
            <Link to="/login" className="text-red-500 hover:text-red-400 font-medium">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// Forgot Password Page
export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase() })
      });
      setSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ba-black flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center shadow-red-glow">
            <span className="font-heading text-white text-xl">BA</span>
          </div>
          <span className="font-heading text-white text-2xl tracking-wide">BLACK ARROW</span>
        </Link>

        <div className="bg-ba-surface border border-ba-border rounded-sm p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl text-white mb-2">CHECK YOUR EMAIL</h2>
              <p className="text-zinc-400 mb-6">
                If an account exists for <span className="text-white">{email}</span>, you'll receive a password reset link shortly.
              </p>
              <Link to="/login">
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider">
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl text-white mb-2 text-center">FORGOT PASSWORD</h2>
              <p className="text-zinc-400 text-center mb-6">
                Enter your email and we'll send you a reset link
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-12 bg-ba-black border-ba-border focus:border-red-600"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <p className="mt-6 text-center text-zinc-500 text-sm">
                Remember your password?{' '}
                <Link to="/login" className="text-red-500 hover:text-red-400">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Reset Password Page
export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [email, setEmail] = useState('');

  // Get token from URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  // Verify token on mount
  React.useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    
    const verifyToken = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/auth/verify-reset-token/${token}`);
        const data = await response.json();
        
        if (response.ok && data.valid) {
          setTokenValid(true);
          setEmail(data.email);
        } else {
          setTokenValid(false);
        }
      } catch (error) {
        setTokenValid(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Password reset successfully!');
        navigate('/login');
      } else {
        // Handle Pydantic validation errors
        let errorMsg = 'Failed to reset password';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map(err => err.msg || err.message || err).filter(Boolean).join('. ') || errorMsg;
          }
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-ba-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // Invalid token
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-ba-black flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="font-heading text-2xl text-white mb-2">INVALID OR EXPIRED LINK</h2>
          <p className="text-zinc-400 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password">
            <Button className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider">
              Request New Link
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ba-black flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center shadow-red-glow">
            <span className="font-heading text-white text-xl">BA</span>
          </div>
          <span className="font-heading text-white text-2xl tracking-wide">BLACK ARROW</span>
        </Link>

        <div className="bg-ba-surface border border-ba-border rounded-sm p-8">
          <h2 className="font-heading text-2xl text-white mb-2 text-center">RESET PASSWORD</h2>
          <p className="text-zinc-400 text-center mb-6">
            Enter a new password for <span className="text-white">{email}</span>
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="pl-10 pr-10 h-12 bg-ba-black border-ba-border focus:border-red-600"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="pl-10 h-12 bg-ba-black border-ba-border focus:border-red-600"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Email Verification Page
export const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already'
  const [email, setEmail] = useState('');

  // Get token from URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  React.useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    
    const verifyEmail = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/auth/verify-email/${token}`);
        const data = await response.json();
        
        if (response.ok) {
          setEmail(data.email);
          setStatus(data.already_verified ? 'already' : 'success');
        } else {
          setStatus('error');
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-ba-black flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center shadow-red-glow">
            <span className="font-heading text-white text-xl">BA</span>
          </div>
          <span className="font-heading text-white text-2xl tracking-wide">BLACK ARROW</span>
        </Link>

        {status === 'verifying' && (
          <div>
            <div className="spinner mx-auto mb-4" />
            <p className="text-zinc-400">Verifying your email...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-ba-surface border border-ba-border rounded-sm p-8">
            <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="font-heading text-2xl text-white mb-2">EMAIL VERIFIED</h2>
            <p className="text-zinc-400 mb-6">
              Your email <span className="text-white">{email}</span> has been verified. You can now access all features.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider"
            >
              Continue to Login
            </Button>
          </div>
        )}

        {status === 'already' && (
          <div className="bg-ba-surface border border-ba-border rounded-sm p-8">
            <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-blue-500" />
            </div>
            <h2 className="font-heading text-2xl text-white mb-2">ALREADY VERIFIED</h2>
            <p className="text-zinc-400 mb-6">
              Your email is already verified. You're all set!
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider"
            >
              Go to Login
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-ba-surface border border-ba-border rounded-sm p-8">
            <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-heading text-2xl text-white mb-2">VERIFICATION FAILED</h2>
            <p className="text-zinc-400 mb-6">
              This verification link is invalid or has expired.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider"
            >
              Go to Login
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};
